import { AddUpdateTaskParams } from '@nextforce/api'
import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import { EditTaskScreenProps } from 'home/stack/types'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import { errorAlert } from 'shared/alerts'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderTitle } from 'shared/header/header_title'
import { useRecentStoresContext } from 'stores/context'
import { useStores } from 'stores/queries/list'
import { useAddEditTaskContext } from 'tasks/context'
import { useAddTask } from 'tasks/queries/add'
import { useTask } from 'tasks/queries/detail'
import { useUpdateTask } from 'tasks/queries/update'
import { RecurrentTaskUpdateType } from 'tasks/types'
import { useUserList } from 'users/queries/list'
import { EditTaskView } from './views'

export function EditTaskScreen({ navigation, route }: EditTaskScreenProps) {
  const { data: stores } = useStores(data => data.stores)
  const { activeStoreId } = useRecentStoresContext()
  const { handleNavigation } = useRoutingContext()
  const {
    taskId,
    setTaskId,
    taskFields,
    setTaskFields,
    setTaskFieldsByTaskDetails,
    isIdenticalEditTaskFields,
    isValid,
    resetTaskFields,
  } = useAddEditTaskContext()

  const taskQuery = useTask(
    route.params?.taskId,
    { fromDate: route.params.fromDate, toDate: route.params.toDate },
    !!route.params?.taskId
  )

  const type = route.params?.taskId ? 'edit' : 'add'

  const canSubmit = useMemo(() => {
    if (type === 'edit') {
      return isValid && !isIdenticalEditTaskFields(taskQuery.data)
    }
    return isValid
  }, [type, taskQuery.data, isIdenticalEditTaskFields, isValid])

  useEffect(() => {
    if (route.params?.taskId) {
      setTaskId(route.params.taskId)
    }
  }, [route.params?.taskId, setTaskId])

  useEffect(() => {
    if (taskQuery.data) {
      setTaskFieldsByTaskDetails(taskQuery.data)
      return () => {
        resetTaskFields()
      }
    }
  }, [
    taskQuery.data,
    setTaskFields,
    setTaskFieldsByTaskDetails,
    resetTaskFields,
  ])

  const activeStore = stores?.find(s => s.id === activeStoreId)

  const [loading, setLoading] = useState<boolean>(false)
  const [updateType, setUpdateType] = useState<RecurrentTaskUpdateType>('THIS')

  const addTaskMutation = useAddTask()
  const editTaskMutation = useUpdateTask()

  const usersQuery = useUserList({}, activeStore?.id)

  const onSetTaskFields = useCallback(
    (params: AddUpdateTaskParams) => {
      setTaskFields(prev => ({ ...prev, ...params }))
    },
    [setTaskFields]
  )

  const onPressRecurring = useCallback(() => {
    handleNavigation({ routeName: 'edit_recurrent_task', routeParams: {} })
  }, [handleNavigation])

  const onPressAssignUser = useCallback(() => {
    handleNavigation({
      routeName: 'task_user_list',
      routeParams: { type: 'assign' },
    })
  }, [handleNavigation])

  const onAddTask = useCallback(async () => {
    setLoading(true)
    try {
      const newTask = await addTaskMutation.mutateAsync({
        ...taskFields,
      })
      const latestOccurence = newTask.occurrences[0]
      setLoading(false)

      handleNavigation({
        routeName: 'schedule',
        routeParams: {
          date: latestOccurence.dueDate,
        },
      })
      resetTaskFields()
    } catch (err: any) {
      setLoading(false)
      errorAlert(err, t('addTaskErrorTitle'))
    }
  }, [addTaskMutation, handleNavigation, resetTaskFields, taskFields])

  const onEditTask = useCallback(async () => {
    if (!taskId) {
      return
    }
    setLoading(true)
    try {
      const updatedTask = await editTaskMutation.mutateAsync({
        id: taskId,
        data: taskFields,
        exception_id: taskQuery.data?.occurrences?.[0]?.exceptionId,
        update_type: updateType,
      })
      const latestOccurence = updatedTask.occurrences[0]
      setLoading(false)
      handleNavigation({
        routeName: 'schedule',
        routeParams: {
          date: latestOccurence.dueDate,
        },
      })
      resetTaskFields()
    } catch (err: any) {
      setLoading(false)
      errorAlert(err, t('editTaskErrorTitle'))
    }
  }, [
    taskId,
    editTaskMutation,
    taskFields,
    taskQuery.data?.occurrences,
    updateType,
    handleNavigation,
    resetTaskFields,
  ])

  useLayoutEffect(() => {
    showHeaderTitle(
      navigation,
      route.params?.taskId ? t('editTask') : t('addTask')
    )
  }, [navigation, route.params?.taskId])

  return (
    <StatusBoundary error={taskQuery.error} isLoading={usersQuery.isLoading}>
      <EditTaskView
        type={type}
        taskFields={taskFields}
        loading={loading}
        onSetTaskFields={onSetTaskFields}
        onSubmit={type === 'edit' ? onEditTask : onAddTask}
        users={usersQuery.data?.users}
        onPressRecurring={onPressRecurring}
        canSubmit={canSubmit}
        updateType={updateType}
        setUpdateType={setUpdateType}
        onPressAssignUser={onPressAssignUser}
      />
    </StatusBoundary>
  )
}
